import { LocationAlertVariant } from '@/components/content/Header/parts/LocationAlert/hooks/useLocationAlert';
import {
	locationAlertBodySX,
	locationAlertCardSX,
	locationAlertCloseSX,
	locationAlertIconContainerSX,
	locationAlertIconSX,
	locationAlertLinkSX,
	locationAlertMessageSX,
	locationAlertPopperSX,
} from '@/components/content/Header/parts/LocationAlert/styles/locationAlertStyles';
import DynamicIcon from '@/components/icons/DynamicIcon';
import { useLocalization } from '@/data/Localization';
import { Box, ClickAwayListener, IconButton, Link, Popper, Typography } from '@mui/material';
import { StoreIcon } from 'presentation/svgImages/StoreIcon';
import { FC, KeyboardEvent } from 'react';
import { MATERIAL_SYMBOL_ICONS } from 'shared/custom/constants/commonConstants';

type Props = {
	variant: LocationAlertVariant;
	anchorEl: HTMLElement | null;
	isVisible: boolean;
	onAction: () => void;
	onDismiss: () => void;
};

export const LocationAlert: FC<Props> = ({ variant, anchorEl, isVisible, onAction, onDismiss }) => {
	const localization = useLocalization('DeliveryLocator').LocationAlert;
	const isLocation = variant === 'location';

	const handleKeyDown = (event: KeyboardEvent) => {
		if (event.key === 'Escape') onDismiss();
	};

	const handleAction = () => {
		onDismiss();
		onAction();
	};

	return (
		<Popper
			open={isVisible && !!anchorEl}
			anchorEl={anchorEl}
			placement={isLocation ? 'bottom-end' : 'bottom'}
			sx={locationAlertPopperSX}
			modifiers={[{ name: 'preventOverflow', options: { padding: 8 } }]}
		>
			<ClickAwayListener onClickAway={onDismiss}>
				<Box
					id={isLocation ? 'locationMismatchAlert' : 'storeMismatchAlert'}
					className={`location-alert ${
						isLocation ? 'location-alert--location' : 'location-alert--store'
					}`}
					role="alert"
					onKeyDown={handleKeyDown}
					sx={locationAlertCardSX}
				>
					<Box display="flex" gap="5px" alignItems="center">
						<Box
							className="location-alert__icon"
							aria-hidden="true"
							sx={locationAlertIconContainerSX}
						>
							{isLocation ? (
								<DynamicIcon
									name={MATERIAL_SYMBOL_ICONS.locationOn}
									variant="rounded"
									size={24}
									sx={{ color: 'primary.main' }}
								/>
							) : (
								<StoreIcon sx={locationAlertIconSX} />
							)}
						</Box>
						<Box sx={locationAlertBodySX}>
							<Typography
								variant="bodyBaseline"
								className="location-alert__message"
								sx={locationAlertMessageSX}
							>
								{isLocation ? localization.locationMessage.t() : localization.storeMessage.t()}
							</Typography>
							<IconButton
								id="mismatchAlertClose"
								className="location-alert__close"
								aria-label={localization.close.t()}
								onClick={onDismiss}
								sx={locationAlertCloseSX}
								disableRipple
							>
								<DynamicIcon name={MATERIAL_SYMBOL_ICONS.close} variant="rounded" size={24} />
							</IconButton>
						</Box>
					</Box>
					<Link
						component="button"
						type="button"
						id="mismatchAlertActionLink"
						className="location-alert__link"
						variant="bodyBaseline"
						underline="none"
						onClick={handleAction}
						sx={locationAlertLinkSX}
					>
						{isLocation ? localization.locationLink.t() : localization.storeLink.t()}
					</Link>
				</Box>
			</ClickAwayListener>
		</Popper>
	);
};
